import { runtime } from "../core/runtime.js";
import { t } from "../core/i18n.js";
import { persist } from "../core/model.js";
import { icon } from "./icons.js";
import { render } from "./views.js";
import { updateTimerDOM } from "./timers.js";

const MAX_SESSIONS = 100;
const limits = (key) => [key === "rest" ? 0 : 1, key === "repeats" ? 100 : 600];
const sessionLabel = (index) => t("Session {number}", { number: index + 1 });
const totals = (blocks) => ({
  count: blocks.reduce((sum, block) => sum + block.repeats, 0),
  minutes: blocks.reduce((sum, block) => sum + block.work * block.repeats, 0),
});
const summary = (blocks) =>
  t("{count} sessions · {minutes} min", totals(blocks));

function field(block, index, key, label) {
  const [min, max] = limits(key);
  return `<input type="number" inputmode="numeric" min="${min}" max="${max}" step="1" value="${block[key]}" data-seq="${index}" data-key="${key}" aria-label="${t(label)}, ${sessionLabel(index)}">`;
}

function sessionRow(block, index, count) {
  return `<div class="pf-session"><span class="pf-session-n" aria-hidden="true">${String(index + 1).padStart(2, "0")}</span>${field(block, index, "work", "Focus time")}${field(block, index, "rest", "Break time")}${field(block, index, "repeats", "Repeat this session")}<button type="button" class="pf-session-remove" data-remove-block="${index}" aria-label="${t("Delete {session}", { session: sessionLabel(index) })}" title="${t("Delete {session}", { session: sessionLabel(index) })}" ${count <= 1 ? "disabled" : ""}>${icon("trash")}</button></div>`;
}

export function renderSequenceSettings() {
  const { blocks, loop } = runtime.state.pom;
  const changesLater = runtime.focusTimer && !runtime.focusTimer.finished;
  return `<section class="pf-sessions" aria-labelledby="sessionSettingsTitle"><div class="kun-section-head"><h2 id="sessionSettingsTitle">${t("Session settings")}</h2><span data-session-summary>${summary(blocks)}</span></div>
    <div class="pf-session-head" aria-hidden="true"><span></span><span>${t("Focus")}</span><span>${t("Break")}</span><span>×</span><span></span></div>
    <div class="pf-session-list">${blocks.map((block, index) => sessionRow(block, index, blocks.length)).join("")}</div>
    <p class="pf-unit">${t("Minutes; × is how many times it repeats.")}</p>
    <button type="button" class="kun-add" data-add-block ${blocks.length >= MAX_SESSIONS ? "disabled" : ""}>${icon("plus")}${t("Add session")}</button>
    <label class="pf-pref pf-loop"><input type="checkbox" class="pf-check" data-pref-check="loop" ${loop ? "checked" : ""}><span>${t("Repeat sequence")}<small>${t("Start again after the last session.")}</small></span></label>
    ${changesLater ? `<p class="pf-note">${t("Changes apply to your next sequence.")}</p>` : ""}
  </section>`;
}

function updateSettings(index) {
  const { blocks } = runtime.state.pom;
  persist();
  const total = document.querySelector("[data-session-summary]");
  if (total) total.textContent = summary(blocks);
  document.querySelectorAll(`[data-seq="${index}"]`).forEach((input) => {
    input.value = blocks[index][input.dataset.key];
  });
  if (!runtime.focusTimer) updateTimerDOM();
}

function focusSessionField(index) {
  document
    .querySelector(`[data-seq="${index}"][data-key="work"]`)
    ?.focus({ preventScroll: true });
}

export function bindSequenceSettings() {
  document.querySelectorAll("[data-seq]").forEach(
    (input) =>
      (input.onchange = () => {
        const index = +input.dataset.seq,
          key = input.dataset.key,
          [min, max] = limits(key);
        runtime.state.pom.blocks[index][key] = Math.max(
          min,
          Math.min(max, Math.round(+input.value || 0)),
        );
        updateSettings(index);
      }),
  );
  document.querySelectorAll("[data-remove-block]").forEach(
    (button) =>
      (button.onclick = () => {
        const { blocks } = runtime.state.pom;
        if (blocks.length <= 1) return;
        const index = +button.dataset.removeBlock;
        blocks.splice(index, 1);
        persist();
        render();
        focusSessionField(Math.min(index, blocks.length - 1));
      }),
  );
  document.querySelector("[data-add-block]")?.addEventListener("click", () => {
    const { blocks } = runtime.state.pom;
    if (blocks.length >= MAX_SESSIONS) return;
    // A new session starts from the previous one, which is usually what people repeat.
    const last = blocks[blocks.length - 1] || { work: 25, rest: 5 };
    blocks.push({ work: last.work, rest: last.rest, repeats: 1 });
    persist();
    render();
    focusSessionField(blocks.length - 1);
  });
}
